import OpenAI, {
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';
import { supabase } from '../lib/supabase';

/**
 * Maps OpenAI API error types to user-friendly error messages.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

// Initialize OpenAI client
const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
});

/**
 * Fetches analytics data from Supabase for AI analysis
 */
export async function fetchAnalyticsData(dateRange = 30) {
  try {
    const startDate = new Date();
    startDate?.setDate(startDate?.getDate() - dateRange);

    // Fetch audit logs
    const { data: auditLogs, error: auditError } = await supabase?.from('application_logs')?.select('*')?.gte('created_at', startDate?.toISOString())?.order('created_at', { ascending: false })?.limit(500);

    if (auditError) throw auditError;

    // Fetch compliance alerts
    const { data: complianceAlerts, error: complianceError } = await supabase?.from('compliance_alerts')?.select('*, companies(*)')?.gte('created_at', startDate?.toISOString())?.order('created_at', { ascending: false })?.limit(200);

    if (complianceError) throw complianceError;

    // Fetch treatment analytics
    const { data: treatmentAnalytics, error: treatmentError } = await supabase?.from('treatment_analytics')?.select('*, products(name, category), companies(*)')?.gte('created_at', startDate?.toISOString())?.order('created_at', { ascending: false })?.limit(300);

    if (treatmentError) throw treatmentError;

    return {
      auditLogs: auditLogs || [],
      complianceAlerts: complianceAlerts || [],
      treatmentAnalytics: treatmentAnalytics || [],
    };
  } catch (error) {
    console.error('Error fetching analytics data:', error);
    throw new Error('Failed to fetch analytics data');
  }
}

/**
 * Generates AI-powered strategic recommendations using GPT-5
 */
export async function generateStrategicRecommendations(analyticsData) {
  try {
    const prompt = `Analyze the following farm operations data and provide strategic recommendations:

Audit Logs Summary:
- Total Events: ${analyticsData?.auditLogs?.length}
- Categories: ${[...new Set(analyticsData.auditLogs.map(log => log.category))]?.join(', ')}
- Critical Events: ${analyticsData?.auditLogs?.filter(log => log?.level === 'critical')?.length}

Compliance Alerts:
- Total Alerts: ${analyticsData?.complianceAlerts?.length}
- Unresolved: ${analyticsData?.complianceAlerts?.filter(alert => !alert?.is_resolved)?.length}
- High Severity: ${analyticsData?.complianceAlerts?.filter(alert => alert?.severity === 'high' || alert?.severity === 'critical')?.length}

Treatment Analytics:
- Total Treatments: ${analyticsData?.treatmentAnalytics?.length}
- Average Effectiveness: ${(analyticsData?.treatmentAnalytics?.reduce((sum, t) => sum + (t?.effectiveness_rating || 0), 0) / analyticsData?.treatmentAnalytics?.length)?.toFixed(2)}
- Products Used: ${[...new Set(analyticsData.treatmentAnalytics.map(t => t.products?.name).filter(Boolean))]?.length}

Please provide:
1. Top 3 strategic recommendations for improving operations
2. Identified risk areas requiring immediate attention
3. Optimization opportunities for treatment effectiveness
4. Compliance improvement suggestions
5. Resource allocation recommendations`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        {
          role: 'system',
          content: 'You are an expert agricultural operations analyst specializing in IPM strategies, compliance management, and operational efficiency. Provide data-driven, actionable insights.'
        },
        { role: 'user', content: prompt }
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'strategic_recommendations',
          schema: {
            type: 'object',
            properties: {
              top_recommendations: {
                type: 'array',
                items: { type: 'string' },
                description: 'Top 3 strategic recommendations'
              },
              risk_areas: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    area: { type: 'string' },
                    severity: { type: 'string', enum: ['high', 'medium', 'low'] },
                    description: { type: 'string' }
                  },
                  required: ['area', 'severity', 'description']
                }
              },
              optimization_opportunities: {
                type: 'array',
                items: { type: 'string' }
              },
              compliance_suggestions: {
                type: 'array',
                items: { type: 'string' }
              },
              resource_allocation: {
                type: 'object',
                properties: {
                  recommendations: { type: 'array', items: { type: 'string' } },
                  estimated_roi: { type: 'string' }
                },
                required: ['recommendations']
              },
              confidence_score: {
                type: 'number',
                minimum: 0,
                maximum: 1,
                description: 'Confidence level in recommendations (0-1)'
              }
            },
            required: ['top_recommendations', 'risk_areas', 'optimization_opportunities', 'compliance_suggestions', 'resource_allocation', 'confidence_score'],
            additionalProperties: false
          }
        }
      },
      reasoning_effort: 'high',
      verbosity: 'medium'
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error generating strategic recommendations:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Detects anomalies in audit logs, compliance patterns, and treatment trends
 */
export async function detectAnomalies(analyticsData) {
  try {
    // Prepare data summary for AI analysis
    const auditPatterns = analyticsData?.auditLogs?.reduce((acc, log) => {
      const hour = new Date(log.created_at)?.getHours();
      const key = `${log?.category}_hour_${hour}`;
      acc[key] = (acc?.[key] || 0) + 1;
      return acc;
    }, {});

    const compliancePatterns = analyticsData?.complianceAlerts?.reduce((acc, alert) => {
      acc[alert.severity] = (acc?.[alert?.severity] || 0) + 1;
      return acc;
    }, {});

    const treatmentPatterns = analyticsData?.treatmentAnalytics?.reduce((acc, treatment) => {
      if (treatment?.effectiveness_rating) {
        acc.effectiveness = acc?.effectiveness || [];
        acc?.effectiveness?.push(treatment?.effectiveness_rating);
      }
      if (treatment?.cost_per_acre) {
        acc.costs = acc?.costs || [];
        acc?.costs?.push(treatment?.cost_per_acre);
      }
      return acc;
    }, {});

    const prompt = `Analyze these operational patterns and identify anomalies:

Audit Log Patterns:
${JSON.stringify(auditPatterns, null, 2)}

Compliance Alert Distribution:
${JSON.stringify(compliancePatterns, null, 2)}

Treatment Effectiveness (range):
- Min: ${Math.min(...(treatmentPatterns?.effectiveness || [0]))}
- Max: ${Math.max(...(treatmentPatterns?.effectiveness || [0]))}
- Average: ${(treatmentPatterns?.effectiveness?.reduce((a, b) => a + b, 0) / (treatmentPatterns?.effectiveness?.length || 1))?.toFixed(2)}

Treatment Costs per Acre:
- Min: $${Math.min(...(treatmentPatterns?.costs || [0]))}
- Max: $${Math.max(...(treatmentPatterns?.costs || [0]))}
- Average: $${(treatmentPatterns?.costs?.reduce((a, b) => a + b, 0) / (treatmentPatterns?.costs?.length || 1))?.toFixed(2)}

Identify:
1. Unusual activity patterns in audit logs
2. Compliance alert spikes or concerning trends
3. Treatment effectiveness outliers
4. Cost anomalies
5. Potential security or operational concerns`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        {
          role: 'system',
          content: 'You are an expert data analyst specializing in anomaly detection for agricultural operations. Focus on identifying patterns that deviate from normal operations and may indicate issues requiring attention.'
        },
        { role: 'user', content: prompt }
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'anomaly_detection',
          schema: {
            type: 'object',
            properties: {
              anomalies: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    type: { type: 'string', enum: ['audit_log', 'compliance', 'treatment_effectiveness', 'cost', 'security'] },
                    severity: { type: 'string', enum: ['critical', 'high', 'medium', 'low'] },
                    description: { type: 'string' },
                    detected_at: { type: 'string' },
                    recommended_action: { type: 'string' },
                    confidence_score: { type: 'number', minimum: 0, maximum: 1 }
                  },
                  required: ['type', 'severity', 'description', 'recommended_action', 'confidence_score']
                }
              },
              summary: { type: 'string' },
              overall_risk_level: { type: 'string', enum: ['critical', 'high', 'medium', 'low'] }
            },
            required: ['anomalies', 'summary', 'overall_risk_level'],
            additionalProperties: false
          }
        }
      },
      reasoning_effort: 'high',
      verbosity: 'medium'
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error detecting anomalies:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Generates predictive insights for seasonal planning
 */
export async function generatePredictiveInsights(analyticsData) {
  try {
    const currentMonth = new Date()?.getMonth();
    const monthlyTrends = analyticsData?.treatmentAnalytics?.reduce((acc, treatment) => {
      const month = new Date(treatment.created_at)?.getMonth();
      acc[month] = acc?.[month] || { count: 0, totalEffectiveness: 0, totalCost: 0 };
      acc[month].count++;
      acc[month].totalEffectiveness += treatment?.effectiveness_rating || 0;
      acc[month].totalCost += treatment?.cost_per_acre || 0;
      return acc;
    }, {});

    const prompt = `Based on historical treatment data, provide predictive insights:

Current Month: ${new Date()?.toLocaleString('default', { month: 'long' })}

Monthly Treatment Trends:
${Object.entries(monthlyTrends)?.map(([month, data]) => {
  const monthName = new Date(2025, parseInt(month), 1)?.toLocaleString('default', { month: 'long' });
  return `${monthName}: ${data?.count} treatments, Avg Effectiveness: ${(data?.totalEffectiveness / data?.count)?.toFixed(2)}, Avg Cost: $${(data?.totalCost / data?.count)?.toFixed(2)}`;
})?.join('\n')}

Recent Compliance Issues: ${analyticsData?.complianceAlerts?.filter(a => !a?.is_resolved)?.length} unresolved

Provide:
1. Seasonal planning recommendations for next 90 days
2. Predicted resource needs
3. Compliance forecasting
4. Treatment optimization strategies
5. Risk mitigation approaches`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        {
          role: 'system',
          content: 'You are an agricultural planning expert with deep knowledge of IPM strategies, seasonal crop management, and predictive analytics. Provide forward-looking, actionable insights.'
        },
        { role: 'user', content: prompt }
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'predictive_insights',
          schema: {
            type: 'object',
            properties: {
              seasonal_recommendations: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    period: { type: 'string' },
                    recommendations: { type: 'array', items: { type: 'string' } }
                  },
                  required: ['period', 'recommendations']
                }
              },
              resource_predictions: {
                type: 'object',
                properties: {
                  labor: { type: 'string' },
                  materials: { type: 'string' },
                  budget: { type: 'string' }
                },
                required: ['labor', 'materials', 'budget']
              },
              compliance_forecast: {
                type: 'array',
                items: { type: 'string' }
              },
              optimization_strategies: {
                type: 'array',
                items: { type: 'string' }
              },
              risk_mitigation: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    risk: { type: 'string' },
                    mitigation: { type: 'string' },
                    priority: { type: 'string', enum: ['high', 'medium', 'low'] }
                  },
                  required: ['risk', 'mitigation', 'priority']
                }
              }
            },
            required: ['seasonal_recommendations', 'resource_predictions', 'compliance_forecast', 'optimization_strategies', 'risk_mitigation'],
            additionalProperties: false
          }
        }
      },
      reasoning_effort: 'high',
      verbosity: 'high'
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error generating predictive insights:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Natural language query interface for analytics
 */
export async function queryAnalytics(question, analyticsData) {
  try {
    const context = `
Available Data Context:
- Audit Logs: ${analyticsData?.auditLogs?.length} records
- Compliance Alerts: ${analyticsData?.complianceAlerts?.length} records (${analyticsData?.complianceAlerts?.filter(a => !a?.is_resolved)?.length} unresolved)
- Treatment Analytics: ${analyticsData?.treatmentAnalytics?.length} records

Recent Trends:
- Most common audit category: ${Object.entries(analyticsData?.auditLogs?.reduce((acc, log) => {
      acc[log.category] = (acc?.[log?.category] || 0) + 1;
      return acc;
    }, {}))?.sort((a, b) => b?.[1] - a?.[1])?.[0]?.[0] || 'N/A'}
- Average treatment effectiveness: ${(analyticsData?.treatmentAnalytics?.reduce((sum, t) => sum + (t?.effectiveness_rating || 0), 0) / analyticsData?.treatmentAnalytics?.length)?.toFixed(2)}
- Critical compliance alerts: ${analyticsData?.complianceAlerts?.filter(a => a?.severity === 'critical')?.length}`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        {
          role: 'system',
          content: `You are an AI analytics assistant for farm operations. Answer questions about audit logs, compliance, and treatment analytics. Provide specific, data-driven answers based on the context provided. ${context}`
        },
        { role: 'user', content: question }
      ],
      reasoning_effort: 'medium',
      verbosity: 'medium',
      max_completion_tokens: 1000
    });

    return response?.choices?.[0]?.message?.content;
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error querying analytics:', error);
    }
    throw new Error(errorInfo.message);
  }
}

// Export service object with all methods
const aiAnalyticsService = {
  fetchAnalyticsData,
  generateStrategicRecommendations,
  detectAnomalies,
  generatePredictiveInsights,
  queryAnalytics
};

export { aiAnalyticsService };